import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, KeyRound, X, ShieldAlert } from "lucide-react";

const empty = { email: "", name: "", role: "kasiyer", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [security, setSecurity] = useState(null);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(null);
  const [form, setForm] = useState({ ...empty });
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    const [u, s] = await Promise.all([
      api.get("/users"),
      api.get("/auth/security-status").catch(() => ({ data: null })),
    ]);
    setItems(u.data);
    setSecurity(s.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Kullanıcı eklendi");
      setOpen(false);
      setForm({ ...empty });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success("Rol güncellendi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const resetPw = async () => {
    try {
      await api.post(`/users/${pwOpen.id}/reset-password`, { new_password: newPw });
      toast.success(`Parola sıfırlandı: ${pwOpen.name}. Kullanıcı ilk girişte yeniden değiştirmek zorunda kalacak.`);
      setPwOpen(null);
      setNewPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const del = async (u) => {
    if (!confirm(`${u.name} silinecek. Emin misiniz?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const roleColor = (r) => r === "admin" ? "text-[--gold]" : r === "kasiyer" ? "text-emerald-400" : "text-neutral-400";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-caps">Kullanıcı Yönetimi</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Ekip</h1>
        </div>
        <button onClick={()=>setOpen(true)} className="btn-primary-gold flex items-center gap-2" data-testid="add-user-btn"><Plus size={14} /> Yeni Kullanıcı</button>
      </div>

      {security && security.count > 0 && (
        <div className="card-flat p-6 border-red-500/40" style={{borderColor:"rgba(239,68,68,0.4)"}} data-testid="security-banner">
          <div className="flex items-start gap-4">
            <ShieldAlert size={24} className="text-red-400 shrink-0 mt-1" />
            <div className="flex-1">
              <div className="font-display text-xl text-red-400">Güvenlik Uyarısı — {security.count} kullanıcı hâlâ varsayılan parola kullanıyor</div>
              <div className="text-sm text-neutral-400 mt-2">
                Aşağıdaki hesaplar internete açık ortamda ele geçirilebilir. Hemen parolalarını sıfırlayın:
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {security.insecure_users.map(u => (
                  <div key={u.email} className="border border-red-500/40 px-3 py-1 text-xs font-mono">{u.email}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card-flat overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-neutral-500 label-caps border-b border-white/5">
              <th className="text-left px-6 py-4">Ad</th>
              <th className="text-left px-6 py-4">E-posta</th>
              <th className="text-left px-6 py-4">Rol</th>
              <th className="text-left px-6 py-4">Durum</th>
              <th className="text-right px-6 py-4">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[.02]" data-testid={`user-row-${u.id}`}>
                <td className="px-6 py-4">{u.name} {u.id === me?.id && <span className="label-caps ml-2">(Ben)</span>}</td>
                <td className="px-6 py-4 font-mono text-neutral-400">{u.email}</td>
                <td className="px-6 py-4">
                  <select value={u.role} disabled={u.id === me?.id} onChange={(e)=>changeRole(u, e.target.value)} className={`bg-transparent border-0 label-caps ${roleColor(u.role)}`} style={u.role==="admin"?{color:"var(--gold)"}:{}} data-testid={`role-${u.id}`}>
                    <option value="admin">Admin</option>
                    <option value="kasiyer">Kasiyer</option>
                    <option value="tezgahtar">Tezgahtar</option>
                  </select>
                </td>
                <td className="px-6 py-4 text-xs">
                  {u.must_change_password ? <span className="text-red-400">Parola sıfırlanacak</span> : <span className="text-emerald-400">Aktif</span>}
                </td>
                <td className="px-6 py-4 text-right">
                  <button onClick={()=>{setPwOpen(u); setNewPw("");}} className="text-neutral-400 hover:text-[--gold] mr-3" title="Parola sıfırla" data-testid={`reset-${u.id}`}><KeyRound size={14} /></button>
                  {u.id !== me?.id && <button onClick={()=>del(u)} className="text-neutral-400 hover:text-red-400" title="Sil" data-testid={`del-user-${u.id}`}><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md card-flat p-8" style={{background:"var(--surface)"}}>
            <div className="flex justify-between items-start mb-6">
              <div><div className="label-caps">Yeni</div><div className="font-display text-2xl mt-1">Kullanıcı Ekle</div></div>
              <button onClick={()=>setOpen(false)}><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="label-caps block mb-2">Ad Soyad</label><input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="new-user-name" /></div>
              <div><label className="label-caps block mb-2">E-posta</label><input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono" data-testid="new-user-email" /></div>
              <div><label className="label-caps block mb-2">Rol</label>
                <select value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} className="w-full border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="new-user-role">
                  <option value="tezgahtar">Tezgahtar</option>
                  <option value="kasiyer">Kasiyer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div><label className="label-caps block mb-2">İlk Parola (min 6)</label><input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono" data-testid="new-user-password" /></div>
            </div>
            <button onClick={create} className="btn-primary-gold w-full mt-6" data-testid="save-new-user">Ekle</button>
          </div>
        </div>
      )}

      {pwOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md card-flat p-8" style={{background:"var(--surface)"}}>
            <div className="flex justify-between items-start mb-6">
              <div><div className="label-caps">Parola Sıfırla</div><div className="font-display text-2xl mt-1">{pwOpen.name}</div></div>
              <button onClick={()=>setPwOpen(null)}><X size={18} /></button>
            </div>
            <input type="password" placeholder="Yeni parola (min 6)" value={newPw} onChange={(e)=>setNewPw(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm font-mono" data-testid="reset-pw-input" />
            <div className="text-xs text-neutral-500 mt-2">Kullanıcı ilk girişte bu parolayı değiştirmek zorunda kalacak.</div>
            <button onClick={resetPw} className="btn-primary-gold w-full mt-6" data-testid="reset-pw-submit">Sıfırla</button>
          </div>
        </div>
      )}
    </div>
  );
}
